import { MUICheckBox, MUISelect } from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import { StatusCodes } from "http-status-codes"
import { MouseEvent, useCallback, useEffect } from "react"
import { Controller, ControllerRenderProps, useForm } from "react-hook-form"
import { Navigate } from "react-router-dom"
import { toast } from "react-toastify"
import { z } from "zod"
import { digitalManagerApi } from "../../../../../../services/api"
import {
  DiMaDetailsContent,
  DiMaDetailsContentProps,
} from "../../../../../components/DiMaDetailsContent"
import {
  ApplicationPropertyGet,
  ApplicationPropertyPut,
  ApplicationPropertyTypeEnum,
} from "../../../../../models/ApplicationProperty"
import {
  AccessLevel,
  DimaFeatureShortNameEnum,
  hasUserAccessToApplicationFeature,
} from "../../../../../models/Authorization"
import { UserInfo } from "../../../../../models/User"

const validationSchema = z.object({
  type: z.nativeEnum(ApplicationPropertyTypeEnum, { message: "Type field is required" }),
  isRequestable: z.boolean(),
})

export function ApplicationPropertyDetails(props: {
  readonly property: ApplicationPropertyGet
  readonly applicationId: string
  readonly user: UserInfo
  readonly setProperty: (updatedProperty: ApplicationPropertyGet) => void
}) {
  const { property, applicationId, user, setProperty } = props
  const returnURL = `/applications/${applicationId}#properties`

  const formMethods = useForm<ApplicationPropertyPut>({
    resolver: zodResolver(validationSchema),
  })

  const { handleSubmit, control, reset } = formMethods

  useEffect(() => {
    reset({
      type: property.type,
      isRequestable: property.isRequestable,
    })
  }, [property, reset])

  const onSubmit = useCallback(
    (values: ApplicationPropertyPut) =>
      digitalManagerApi
        .put<ApplicationPropertyGet>(
          `/applications/${applicationId}/properties/${property.name}`,
          {
            type: values.type,
            isRequestable: values.isRequestable,
          }
        )
        .then((res) => {
          if (res.status === StatusCodes.OK) {
            toast.success("Property updated")
            setProperty(res.data)
          }
        })
        .catch(() => {
          toast.error("Cannot update property")
          throw new Error("Cannot update property")
        }),
    [applicationId, property.name, setProperty]
  )

  const handleTypeChange = useCallback(
    (field: ControllerRenderProps<ApplicationPropertyPut, "type">, values: unknown) => {
      let selected = ""
      if (Array.isArray(values) && values.length > 0) {
        const first = values[0] as string | { id: string }
        selected = typeof first === "string" ? first : first.id
      }
      field.onChange(selected)
    },
    []
  )

  const handleTypeSelectChange = useCallback(
    (field: ControllerRenderProps<ApplicationPropertyPut, "type">) => (values: unknown) =>
      handleTypeChange(field, values),
    [handleTypeChange]
  )

  const renderTypeSelect = useCallback(
    ({ field }: { field: ControllerRenderProps<ApplicationPropertyPut, "type"> }) => (
      <MUISelect
        name={field.name}
        selectedItem={field.value ? [field.value] : []}
        onChange={handleTypeSelectChange(field)}
        dropdownLOV={Object.values(ApplicationPropertyTypeEnum).map((t) => ({
          id: t,
          name: t,
        }))}
        size={32}
        minWidth={250}
        placeholder="Select type"
        isDisabled={
          !hasUserAccessToApplicationFeature(
            user.authorizations.applications,
            applicationId,
            DimaFeatureShortNameEnum.AM,
            AccessLevel.Write
          )
        }
      />
    ),
    [applicationId, user.authorizations.applications, handleTypeSelectChange]
  )

  const handleCheckboxToggle = useCallback(
    (
      e: MouseEvent<HTMLElement>,
      field: ControllerRenderProps<ApplicationPropertyPut, "isRequestable">
    ) => {
      e.stopPropagation()
      field.onChange(!field.value)
    },
    []
  )

  const handleCheckboxToggleForField = useCallback(
    (field: ControllerRenderProps<ApplicationPropertyPut, "isRequestable">) =>
      (e: MouseEvent<HTMLElement>) =>
        handleCheckboxToggle(e, field),
    [handleCheckboxToggle]
  )

  const renderCheckbox = useCallback(
    ({
      field,
    }: {
      field: ControllerRenderProps<ApplicationPropertyPut, "isRequestable">
    }) => (
      <MUICheckBox
        {...field}
        id="isRequestable"
        checked={field.value}
        handleToggle={handleCheckboxToggleForField(field)}
        disabled={
          !hasUserAccessToApplicationFeature(
            user.authorizations.applications,
            applicationId,
            DimaFeatureShortNameEnum.AM,
            AccessLevel.Write
          )
        }
      />
    ),
    [applicationId, user.authorizations.applications, handleCheckboxToggleForField]
  )

  if (!applicationId) {
    return <Navigate to={returnURL} />
  }

  const canWrite = hasUserAccessToApplicationFeature(
    user.authorizations.applications,
    applicationId,
    DimaFeatureShortNameEnum.AM,
    AccessLevel.Write
  )

  const detailsContentProps: DiMaDetailsContentProps<ApplicationPropertyPut> = {
    formMethods,
    canWrite,
    inlineEdit: true,
    listItems: [
      {
        id: 0,
        label: "Name",
        displayItem: <div style={{ width: "100%" }}>{property.name}</div>,
      },
      {
        id: 1,
        label: "Type",
        displayItem: <div style={{ width: "100%" }}>{property.type}</div>,
        editItem: <Controller name="type" control={control} render={renderTypeSelect} />,
      },
      {
        id: 2,
        label: "Is Requestable",
        displayItem: (
          <div style={{ width: "100%" }}>{property.isRequestable ? "true" : "false"}</div>
        ),
        editItem: (
          <Controller name="isRequestable" control={control} render={renderCheckbox} />
        ),
      },
    ],
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DiMaDetailsContent {...detailsContentProps} onSubmit={onSubmit} />
    </form>
  )
}
